import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

type Sample = { id: string; title: string; card: SocialPostPreviewCardMetadata };

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const unsplash = (id: string) => `https://images.unsplash.com/photo-${id}?w=900&q=80&auto=format&fit=crop`;

const PHOTOS: SocialPostPreviewMedia[] = [
  '1517836357463-d25dfeac3438',
  '1534438327276-14e5300c3a48',
  '1571019613454-1cb2f99b2d8b',
  '1518611012118-696072aa579a',
  '1544367567-0f2fcb009e0b',
  '1552674605-db6ffd4facb5',
].map((id) => ({ url: unsplash(id), kind: 'image' }));

const PORTRAIT: SocialPostPreviewMedia = { url: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=900&h=1600&fit=crop', kind: 'image' };
const VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/23056/23056-720.mp4', kind: 'video' };
const BROKEN: SocialPostPreviewMedia = { url: 'https://images.unsplash.com/photo-0000000000000-000000000000?w=900', kind: 'image' };

const CAPTION =
  'We turned two this month, and we could not have done it without you.\n\nWhen we opened Feature Frenzy 2 we had one room, six kettlebells and a playlist that was mostly 90s pop. Today we run 28 classes a week, from Sunrise HIIT to Sunday restorative yoga.\n\nBook at https://www.featurefrenzy2.com/birthday and bring a friend. #FeatureFrenzy2 #fitness #hiit';

const linkedin = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'linkedin',
  networkLabel: 'LinkedIn',
  accountLabel: 'alon@example.com',
  media: [],
  mediaCount: 0,
  postType: 'post',
  decisionId: `li-${id}`,
  taskId: `li-task-${id}`,
  labels: LABELS,
  ...card,
});

const x = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'twitter',
  networkLabel: 'X',
  accountLabel: '@featurefrenzy2',
  media: [],
  mediaCount: 0,
  postType: 'post',
  decisionId: `x-${id}`,
  taskId: `x-task-${id}`,
  labels: LABELS,
  ...card,
});

export const LINKEDIN_SAMPLES: Sample[] = [
  {
    id: 'li-text',
    title: 'LinkedIn: text only, no account name',
    card: linkedin('text', {
      caption: 'We are hiring two weekend front-desk coordinators. Flexible hours, free classes, a great team. Message us to apply.',
    }),
  },
  {
    id: 'li-photo',
    title: 'LinkedIn: one photo, named account',
    card: linkedin('photo', {
      accountName: 'Feature Frenzy 2',
      caption: 'Saturday strength session was on fire. Thank you to everyone who showed up and lifted heavy! #strength',
      media: PHOTOS.slice(0, 1),
      mediaCount: 1,
    }),
  },
  {
    id: 'li-portrait',
    title: 'LinkedIn: one portrait photo',
    card: linkedin('portrait', { accountName: 'Feature Frenzy 2', caption: 'Morning flow, 7:00.', media: [PORTRAIT], mediaCount: 1 }),
  },
  {
    id: 'li-two',
    title: 'LinkedIn: two photos',
    card: linkedin('two', { caption: 'Before and after: the new yoga room.', media: PHOTOS.slice(0, 2), mediaCount: 2 }),
  },
  {
    id: 'li-three',
    title: 'LinkedIn: three photos',
    card: linkedin('three', { caption: 'Three classes, one weekend.', media: PHOTOS.slice(0, 3), mediaCount: 3 }),
  },
  {
    id: 'li-many',
    title: 'LinkedIn: six photos (+3)',
    card: linkedin('many', {
      caption: 'Highlights from our open day.',
      media: PHOTOS,
      mediaCount: 6,
      postType: 'carousel',
      labels: { ...LABELS, postType: 'Carousel' },
    }),
  },
  {
    id: 'li-video',
    title: 'LinkedIn: one video, scheduled',
    card: linkedin('video', {
      caption: 'Rope day. Ten minutes of skipping, three rounds, no excuses.',
      media: [VIDEO],
      mediaCount: 1,
      postType: 'video',
      scheduledAt: '2026-10-30T07:30:00.000Z',
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'li-link',
    title: 'LinkedIn: link only',
    card: linkedin('link', {
      caption: 'The November timetable is up. Book your spot before it fills.',
      link: 'https://www.featurefrenzy2.com/schedule',
    }),
  },
  { id: 'li-long', title: 'LinkedIn: long caption', card: linkedin('long', { caption: CAPTION, media: PHOTOS.slice(2, 3), mediaCount: 1 }) },
  {
    id: 'li-broken',
    title: 'LinkedIn: photo that fails to load',
    card: linkedin('broken', { caption: 'Holiday hours.', media: [BROKEN], mediaCount: 1 }),
  },
  {
    id: 'li-hebrew',
    title: 'LinkedIn: Hebrew caption',
    card: linkedin('hebrew', {
      caption: 'שיעור יוגה חדש בימי שלישי ב-19:00. מתאים לכל הרמות. #יוגה #FeatureFrenzy2',
      media: PHOTOS.slice(4, 5),
      mediaCount: 1,
    }),
  },
];

export const X_SAMPLES: Sample[] = [
  {
    id: 'x-text',
    title: 'X: text with handle',
    card: x('text', { caption: 'Open late tonight for the street fair. Cinnamon buns until 10pm. #StreetFair' }),
  },
  {
    id: 'x-no-handle',
    title: 'X (network "x"): no handle, no account name',
    card: x('no-handle', { network: 'x', accountLabel: 'Feature Frenzy login', caption: 'Sunrise HIIT starts Monday at 6:15am.' }),
  },
  {
    id: 'x-photo',
    title: 'X: one photo',
    card: x('photo', {
      accountName: 'Feature Frenzy 2',
      caption: 'Saturday strength session was on fire.',
      media: PHOTOS.slice(0, 1),
      mediaCount: 1,
    }),
  },
  { id: 'x-portrait', title: 'X: one portrait photo', card: x('portrait', { caption: 'Morning flow, 7:00.', media: [PORTRAIT], mediaCount: 1 }) },
  { id: 'x-two', title: 'X: two photos', card: x('two', { caption: 'Before and after.', media: PHOTOS.slice(0, 2), mediaCount: 2 }) },
  {
    id: 'x-three',
    title: 'X: three photos',
    card: x('three', { caption: 'Three classes, one weekend.', media: PHOTOS.slice(0, 3), mediaCount: 3 }),
  },
  { id: 'x-four', title: 'X: four photos', card: x('four', { caption: 'Open day.', media: PHOTOS.slice(0, 4), mediaCount: 4 }) },
  {
    id: 'x-video',
    title: 'X: one video, scheduled',
    card: x('video', {
      caption: 'Rope day.',
      media: [VIDEO],
      mediaCount: 1,
      postType: 'video',
      scheduledAt: '2026-10-30T07:30:00.000Z',
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'x-link',
    title: 'X: link only',
    card: x('link', { caption: 'The November timetable is up.', link: 'https://www.featurefrenzy2.com/schedule' }),
  },
  { id: 'x-long', title: 'X: long post', card: x('long', { caption: CAPTION }) },
  { id: 'x-broken', title: 'X: photo that fails to load', card: x('broken', { caption: 'Holiday hours.', media: [BROKEN], mediaCount: 1 }) },
];
